import React, { useState, useCallback, useRef } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { GetSearchLabCode } from "../config/preauthorizationApi"; // Update import

const LabTestCodeDescription = ({ control, setValue }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [cachedOptions, setCachedOptions] = useState([]);
  const debounceTimeout = useRef(null);

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      if (!inputValue.trim()) {
        callback(cachedOptions);
        return;
      }

      debounceTimeout.current = setTimeout(async () => {
        try {
          setLoading(true);
          const response = await dispatch(
            GetSearchLabCode({ labcde_id: inputValue }) // Updated API call with correct parameter
          ).unwrap();

          const options =
            response.data?.lab_codes?.map((item) => ({
              value: item.loinc_code, // Updated field names
              label: item.loinc_description, // Updated field names
              code: item?.loinc_uom,
            })) || [];

          setCachedOptions(options);
          callback(options);
        } catch (error) {
          console.error("Error fetching lab test codes:", error);
          callback(cachedOptions);
        } finally {
          setLoading(false);
        }
      }, 500);
    },
    [dispatch, cachedOptions]
  );

  return (
    <Controller
      name="lab_test_code" // Updated field name
      control={control}
      rules={{ required: false }}
      render={({ field }) => (
        <AsyncSelect
          {...field}
          inputValue={inputValue}
          onInputChange={(newValue, actionMeta) => {
            setInputValue(newValue);
            return newValue;
          }}
          isClearable
          defaultOptions={cachedOptions}
          loadOptions={loadOptions}
          cacheOptions
          blurInputOnSelect={false}
          menuPlacement="bottom"
          isLoading={loading}
          placeholder="Search lab test codes..." // Updated placeholder
          classNamePrefix="react-select"
          value={
            field.value
              ? {
                  value: field.value.loinc_code, // Updated field names
                  label: field.value.loinc_description, // Updated field names
                  code: field.value.loinc_uom,
                }
              : null
          }
          onChange={(selected) => {
            setValue(
              "lab_test_code", // Updated field name
              selected
                ? {
                    loinc_code: selected.value, // Updated field names
                    loinc_description: selected.label, // Updated field names
                    loinc_uom: selected?.code,
                  }
                : null
            );
            setInputValue("");
          }}
          styles={customSmStyles}
          getOptionLabel={({ label, value }) => (
            <div>{`(${value}) - ${label}`}</div>
          )}
        />
      )}
    />
  );
};

export default LabTestCodeDescription;

// === Styles (same as ServiceCodeSelect) ===
const customSmStyles = {
  control: (base) => ({
    ...base,
    minHeight: "29px",
    fontSize: "0.875rem",
    backgroundColor: "var(--falcon-input-bg)",
    borderColor: "var(--falcon-input-border-color)",
    "&:hover": {
      borderColor: "var(--falcon-input-focus-border-color)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    color: "var(--falcon-input-color)",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "var(--falcon-input-color) !important",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "27px",
    minHeight: "27px",
    svg: {
      color: "var(--falcon-input-color) !important",
    },
  }),
  option: (base) => ({
    ...base,
    paddingTop: 8,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: "0.8rem",
    minHeight: "27px",
    lineHeight: "1.2",
  }),
  menu: (base) => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    backgroundColor: "var(--falcon-card-bg)",
    border: "1px solid var(--falcon-border-color)",
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--falcon-input-placeholder-color)",
    margin: 0,
    padding: 0,
  }),
};
